import { forwardRef, useRef } from 'react'
import { ListItemIcon, MenuItem, Tooltip, Typography } from '@mui/material'
import { useHasEllipsis } from '@global/hooks'
import { UseMeasureRef } from 'react-use/lib/useMeasure'
import * as css from './styles'

interface IProps {
  folders: { id: number; name: string }[]
  onSelected: (id: string) => void
  selectedMenuItem: string | number | null
  height: number
  getDatasetMenuRef: () => UseMeasureRef<HTMLDivElement>
}

type FolderProps = {
  name: string
  isSelected: (item, id) => boolean
  selectedMenuItem: string | number | null
  id: number
} & Pick<IProps, 'onSelected'>

const Folder = ({ name, isSelected, selectedMenuItem, id, onSelected }: FolderProps) => {
  const titleRef = useRef<HTMLSpanElement | null>(null)
  const sectionRef = useRef<HTMLDivElement | null>(null)

  const showTooltip = useHasEllipsis({
    parentElRef: sectionRef,
    childElRef: titleRef,
    offset: 70,
    deps: [name],
  })

  return (
    <css.MenuItem ref={sectionRef} style={{ minWidth: 0 }} data-testid="folder-wrapper">
      {isSelected(selectedMenuItem, id) ? <css.GreenSelectedBar /> : null}
      <MenuItem
        data-testid={name}
        onClick={() => {
          onSelected(String(id))
        }}
        style={{ display: 'flex', flexDirection: 'row' }}
      >
        <ListItemIcon>
          <div style={{ padding: 1, width: 18, height: 18 }} />
        </ListItemIcon>
        <Tooltip placement="top" title={showTooltip ? name : ''} arrow>
          <Typography
            css={[css.typography, isSelected(selectedMenuItem, id) && css.selectedTypography]}
            ref={titleRef}
          >
            {name}
          </Typography>
        </Tooltip>
      </MenuItem>
    </css.MenuItem>
  )
}

export const Folders = forwardRef<HTMLDivElement, IProps>(
  ({ folders, onSelected, selectedMenuItem }: IProps, ref) => {
    const isSelected = (item, id) => String(item) === String(id)

    return (
      <css.Folders ref={ref} data-testid="folders">
        {folders
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(({ id, name }) => (
            <Folder
              key={id}
              id={id}
              name={name}
              isSelected={isSelected}
              selectedMenuItem={selectedMenuItem}
              onSelected={onSelected}
            />
          ))}
      </css.Folders>
    )
  },
)

Folders.displayName = 'Folders'
